import { useRef, useState } from 'react';
import { DocumentStatus, DocumentType } from '@lombok/shared';
import { ApiError, putToPresignedUrl } from '@lombok/api-client';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { Field, SelectField } from '@/components/form';
import { useSessionProfile } from '@/auth/useSessionProfile';
import { api } from '@/lib/api';
import { useConfirmDocument, useDocuments } from '@/data/hooks';
import {
  DOC_ACCEPT,
  DOC_TYPE_OPTIONS,
  documentStatusLabel,
  documentStatusTone,
  documentTypeLabel,
  isAllowedDocContentType,
} from './labels';

/**
 * Documentos (customer onboarding). The customer uploads their RIF / legal / agreement files
 * for verification. Flow: presign a private S3 PUT → upload the file straight to S3 →
 * confirm to persist a PENDING record. Files are listed with their review status and opened
 * through short-lived presigned URLs (the bucket is never public). Admin/finance review
 * (approve/reject) is handled in their own queue; here the customer sees the outcome.
 */
export function DocumentosPage() {
  const { profile } = useSessionProfile();
  const accountId = profile?.customerId;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Documentos"
        description="Sube tus documentos de cuenta para verificación (RIF, documentos legales, acuerdos)."
      />
      {accountId ? (
        <DocumentsForAccount accountId={accountId} />
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-sm text-text-muted">
            Tu usuario no está vinculado a una cuenta de cliente. Contacta a tu vendedor para
            habilitar la carga de documentos.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** Upload form + document list for one account. Reused by the customer's own screen and the staff customer detail. */
export function DocumentsForAccount({
  accountId,
  listTitle = 'Mis documentos',
  emptyDescription = 'Aún no has subido documentos. Usa el formulario para cargar el primero.',
}: {
  accountId: string;
  listTitle?: string;
  emptyDescription?: string;
}) {
  const documents = useDocuments(accountId);
  const confirm = useConfirmDocument(accountId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [docType, setDocType] = useState<DocumentType>(DocumentType.RIF);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError('Selecciona un archivo.');
      return;
    }
    const contentType = file.type;
    if (!isAllowedDocContentType(contentType)) {
      setError('Tipo de archivo no permitido. Usa PNG, JPG, WEBP o PDF.');
      return;
    }
    setUploading(true);
    try {
      const presigned = await api.presignUpload({
        accountId,
        documentType: docType,
        fileName: file.name,
        contentType,
      });
      await putToPresignedUrl(presigned, file, { contentType });
      await confirm.mutateAsync({
        accountId,
        documentId: presigned.documentId,
        s3Key: presigned.s3Key,
        documentType: docType,
        fileName: file.name,
        contentType,
      });
      // Reset the form for the next upload.
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'No se pudo subir el documento. Intenta de nuevo.',
      );
    } finally {
      setUploading(false);
    }
  };

  const openDocument = async (documentId: string) => {
    setError(null);
    setOpening(documentId);
    try {
      const { url } = await api.getDocumentDownloadUrl(accountId, documentId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo abrir el documento.');
    } finally {
      setOpening(null);
    }
  };

  const items = documents.data?.items ?? [];

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
      {/* Upload */}
      <Card>
        <CardHeader>
          <CardTitle>Subir documento</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            {error ? (
              <p
                className="rounded-md border border-danger-soft bg-danger-soft px-3 py-2 text-sm font-medium text-danger"
                role="alert"
              >
                {error}
              </p>
            ) : null}

            <SelectField
              label="Tipo de documento"
              value={docType}
              onChange={(e) => setDocType(e.target.value as DocumentType)}
              options={DOC_TYPE_OPTIONS}
            />

            <Field label="Archivo" htmlFor="doc-file" hint="PNG, JPG, WEBP o PDF (máx. 10 MB).">
              <input
                id="doc-file"
                ref={fileInputRef}
                type="file"
                accept={DOC_ACCEPT}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-text-muted file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-accent-soft file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-primary hover:file:bg-accent-soft/70"
              />
            </Field>

            <Button type="submit" disabled={uploading || !file}>
              {uploading ? 'Subiendo…' : 'Subir documento'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>{listTitle}</CardTitle>
        </CardHeader>
        <QueryState
          isLoading={documents.isLoading}
          isError={documents.isError}
          error={documents.error}
          onRetry={() => void documents.refetch()}
          isEmpty={items.length === 0}
          loadingVariant="table"
          skeletonCols={3}
          emptyTitle="Sin documentos"
          emptyDescription={emptyDescription}
        >
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                <th className="px-4 py-3 font-medium">Documento</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {items.map((doc) => (
                <tr key={doc.documentId} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">
                    <p className="font-medium text-text-primary">{documentTypeLabel(doc.type)}</p>
                    <p className="truncate text-xs text-text-muted">{doc.fileName}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={documentStatusTone(doc.status)}>
                      {documentStatusLabel(doc.status)}
                    </Badge>
                    {doc.reviewedByName ? <p className="mt-1 text-xs text-text-muted">Revisado por {doc.reviewedByName}</p> : null}
                    {doc.status === DocumentStatus.REJECTED && doc.statusReason ? (
                      <p className="mt-1 text-xs text-danger">Motivo: {doc.statusReason}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void openDocument(doc.documentId)}
                      disabled={opening === doc.documentId}
                    >
                      {opening === doc.documentId ? 'Abriendo…' : 'Ver'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </QueryState>
      </Card>
    </div>
  );
}
